// src/AdminPages/AdminLayout/AdminNavbar.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, NavLink, useNavigate } from 'react-router-dom';
import './AdminNavbar.css';
// --- CHANGE 1: Import our new global axios instance ---
import axiosInstance from '../../api/axiosInstance'; 
import AdminFooter from '../AdminFooter/AdminFooter';

const logout = () => {
  localStorage.removeItem('adminToken');
  window.location.href = '/admin';
};

// --- Icons (Original SVG components restored) ---
const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
);

const LessThanIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
);

const ChevronDownIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
);

const AdminNavbar = () => {
  const navigate = useNavigate();

  // --- CHANGE 2: Updated useEffect for Token Verification ---
  // This replaces your old check. It verifies the token with the server.
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('adminToken');
      
      // 1. Basic check: Does token exist?
      if (!token) {
        navigate('/admin', { replace: true });
        return;
      }

      // 2. Advanced check: Is token valid?
      try {
        // We call a protected endpoint. 
        // If the token is expired, the server returns 401.
        // Our axiosInstance interceptor catches the 401 and handles the logout/redirect automatically.
        const res = await axiosInstance.get('/api/admin/me');
        setAdminDetails(res.data.admin);
 
      } catch (error) {
        // If it's NOT a 401 error (e.g. server down), we just log it.
        // The 401 case is already handled by the interceptor.
        if (error.response?.status !== 401) {
            console.error("Token verification check failed (network/server error).");
        }
      }
    };

    verifyToken();
  }, [navigate]);


  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 1024);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const { pathname } = useLocation();
  const dropdownRef = useRef(null);

const [adminDetails, setAdminDetails] = useState({});

  const menuItems = [
    { section: "home", title: "Home", links: [{ name: "Add Slider", path: "/admin/add-slider" }] },
    { section: "achievement", title: "Achievement", links: [{ name: "Add Student Achievement", path: "/admin/add-student-achievement" }] },
    { section: "gallery", title: "Gallery", links: [{ name: "Add Gallery", path: "/admin/add-gallery" }] },
    { section: "news", title: "News", links: [{ name: "Add News", path: "/admin/add-news" }] },
    { section: "youtube", title: "YouTube Videos", links: [{ name: "Add YouTube Video", path: "/admin/add-yt-video" }] },
    { section: "contact", title: "Contact", links: [{ name: "View Contact", path: "/admin/view-contact" }] },
    { section: "admission", title: "Admission Form", links: [{ name: "View Admission Form", path: "/admin/view-admission" }] },
  ];

  // Auto-expand category based on current URL
  useEffect(() => {
    const activeCategory = menuItems.find(category => 
        category.links.some(link => pathname.startsWith(link.path))
    );
    if (activeCategory) {
        setExpandedCategories(prev => ({ ...prev, [activeCategory.section]: true }));
    }
  }, [pathname]);
  
  // Handle window resize for sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) setIsSidebarOpen(false);
      else setIsSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside for user dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleCategory = (key) => setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));

  // Check if a category is active
  const isCategoryActive = (category) => {
    return category.links.some(link => pathname.startsWith(link.path));
  };
  
  return (
    <div className={`admin-full-layout ${isSidebarOpen ? 'sidebar-visible' : 'sidebar-hidden'}`}>
      
      {/* 1. SIDEBAR NAVIGATION */}
      <aside className="admin-side-panel">
        <div className="admin-side-header">
            <span className="admin-side-close-text" onClick={toggleSidebar}>
                CLOSE MENU
            </span>
        </div>
        
        <div className="admin-side-scroll-area">
          {menuItems.map((category) => (
            <div key={category.section} className="admin-side-section">
              <div 
                className={`admin-side-section-btn ${isCategoryActive(category) ? 'active' : ''}`}
                onClick={() => toggleCategory(category.section)}
                aria-expanded={!!expandedCategories[category.section]}
              >
                <span>{category.title}</span>
                <ChevronDownIcon className={`admin-sidebar-arrow-icon ${expandedCategories[category.section] ? 'expanded' : ''}`} />
              </div>
              
              {expandedCategories[category.section] && (
                <div className="admin-side-links-list">
                  {category.links.map(link => (
                    <NavLink 
                      key={link.path} 
                      to={link.path} 
                      className={({ isActive }) => `admin-side-link-item ${isActive ? 'active' : ''}`}
                      onClick={() => { if(window.innerWidth <= 1024) setIsSidebarOpen(false); }}
                    >
                      {link.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="admin-side-footer">
          <button onClick={logout} className="admin-side-logout-action">Logout</button>
        </div>
      </aside>

      {/* 2. MAIN CONTENT AREA */}
      <div className="admin-main-viewport">
        
        {/* TOP NAVBAR */}
        <nav className="admin-top-header">
          <div className="admin-header-left">
            <button className="admin-menu-toggle" onClick={toggleSidebar} aria-label="Toggle Sidebar">
              {isSidebarOpen ? <LessThanIcon /> : <MenuIcon />}
            </button>
            
            <Link to="/admin/dashboard" className="admin-navbar-logo">
              SK COLLEGE
            </Link>
          </div>

          <div className="admin-header-right" ref={dropdownRef}>
            <div className="admin-header-user-text">
              <span className="admin-header-name">{adminDetails?.email}
</span>
              <span className="admin-header-role">{adminDetails?.role}</span>

            </div>
            <button 
                className={`admin-header-profile-circle ${isUserMenuOpen ? 'active' : ''}`} 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-label="Toggle User Menu"
                aria-expanded={isUserMenuOpen}
            >
              <UserIcon />
            </button>

            {isUserMenuOpen && (
              <div className="admin-header-dropdown-box">
                <div className="admin-drop-user-info">
                  <p className="admin-drop-name">{adminDetails?.name}</p>

                  <p className="admin-drop-email">{adminDetails?.email}</p>
                </div>
                <div className="admin-drop-divider"></div>
                <Link to="/admin/change-password" className="admin-drop-link" onClick={() => setIsUserMenuOpen(false)}>Change Password</Link>
                <button onClick={logout} className="admin-drop-link logout-red">Logout</button>
              </div>
            )}
          </div>
        </nav>

        {/* MAIN PAGE WRAPPER */}
        <main className="admin-page-wrapper">
          <div className="admin-content-container">
            <Outlet />
          </div>
          <AdminFooter />
        </main>

        {/* Backdrop overlay for mobile devices */}
        <div className={`admin-mobile-curtain ${isSidebarOpen ? 'show' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>
      </div>
    </div>
  );
};

export default AdminNavbar;